import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { type CurrencyRate, useGetDashboardQuery } from '../api';
import { formatMoney } from '../statuses';
import './merchant-cabinet.css';

type MerchantShellProps = {
  title: string;
  subtitle: string;
  children: React.ReactNode;
  showDashboard?: boolean;
};

const navigation = [
  { to: '/merchant/overview', label: 'Обзор', icon: '◫' },
  { to: '/merchant/applications', label: 'Заявки', icon: '↕' },
  { to: '/merchant/disputes', label: 'Споры', icon: '!' },
  { to: '/merchant/settlements', label: 'Взаиморасчёты', icon: '◌' },
];

export const MerchantShell: React.FC<MerchantShellProps> = ({
  title,
  subtitle,
  children,
  showDashboard = false,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [ratesOpen, setRatesOpen] = useState(showDashboard);
  const { data, isLoading, isError } = useGetDashboardQuery(undefined, {
    pollingInterval: 30_000,
    refetchOnMountOrArgChange: true,
  });

  return (
    <div className="merchant-cabinet">
      <header className="merchant-topbar">
        <NavLink className="merchant-brand" to="/merchant/overview" aria-label="EasyTokenPay: обзор">
          <img src="/logo.svg" alt="EasyTokenPay" />
        </NavLink>
        <button
          className="merchant-menu-toggle"
          type="button"
          aria-expanded={menuOpen}
          aria-controls="merchant-navigation"
          aria-label={menuOpen ? 'Закрыть меню' : 'Открыть меню'}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span /> <span /> <span />
          <span className="sr-only">Открыть меню</span>
        </button>
        <div className="merchant-account">{data?.merchant.username || 'Кабинет мерчанта'}</div>
      </header>

      <aside id="merchant-navigation" className={`merchant-sidebar ${menuOpen ? 'merchant-sidebar--open' : ''}`}>
        <nav aria-label="Основная навигация">
          {navigation.map((item) => (
            <NavLink key={item.to} to={item.to} onClick={() => setMenuOpen(false)}>
              <span aria-hidden="true">{item.icon}</span>{item.label}
            </NavLink>
          ))}
        </nav>
        <button className="merchant-logout" type="button" onClick={() => {
          localStorage.removeItem('token');
          localStorage.removeItem('token_type');
          localStorage.removeItem('refresh_token');
          window.location.assign('/');
        }}>
          Выйти
        </button>
      </aside>

      {menuOpen && <button className="merchant-menu-backdrop" type="button" aria-label="Закрыть меню" onClick={() => setMenuOpen(false)} />}

      <main className="merchant-main">
        <section className="merchant-page-heading">
          <div>
            <p className="merchant-eyebrow">EASYTOKENPAY · МЕРЧАНТ</p>
            <h1>{title}</h1>
            <p>{subtitle}</p>
          </div>
          {data && (
            <div className="merchant-balance-chip">
              <span>Доступно к получению</span>
              <strong>{formatMoney(data.balances.available_to_merchant, data.balances.currency)}</strong>
            </div>
          )}
        </section>

        {isError && <div className="merchant-inline-error">Не удалось обновить сводку. Рабочие разделы остаются доступны.</div>}
        {(showDashboard || data) && (
          <section className="merchant-summary" aria-busy={isLoading}>
            <div className="merchant-summary-head">
              <div>
                <p className="merchant-eyebrow">СОСТОЯНИЕ ПЛАТФОРМЫ</p>
                <h2>Средства и курсы</h2>
              </div>
              <button className="merchant-link-button" type="button" onClick={() => setRatesOpen((open) => !open)}>
                {ratesOpen ? 'Скрыть курсы' : 'Показать курсы'}
              </button>
            </div>
            <div className="merchant-metrics">
              <Metric label="К получению" value={data ? formatMoney(data.balances.available_to_merchant, 'USDT') : '—'} hint="Баланс взаиморасчётов" />
              <Metric label="К возврату" value={data ? formatMoney(data.balances.available_to_platform, 'USDT') : '—'} hint="Перед площадкой" />
              <Metric label="Пополнения" value={data ? String(data.activity.deposits_total) : '—'} hint="За всё время" />
              <Metric label="Открытые споры" value={data ? String(data.activity.open_disputes) : '—'} hint="Требуют решения" warning />
            </div>
            {ratesOpen && <Rates rates={data?.currency_rates || []} loading={isLoading} />}
          </section>
        )}

        {children}
      </main>
    </div>
  );
};

const Metric: React.FC<{ label: string; value: string; hint: string; warning?: boolean }> = ({ label, value, hint, warning }) => (
  <article className={`merchant-metric ${warning ? 'merchant-metric--warning' : ''}`}>
    <span>{label}</span>
    <strong>{value}</strong>
    <small>{hint}</small>
  </article>
);

const Rates: React.FC<{ rates: CurrencyRate[]; loading: boolean }> = ({ rates, loading }) => (
  <div className="merchant-rates">
    {loading && !rates.length && <div className="merchant-skeleton">Обновляем курсы…</div>}
    {!loading && !rates.length && <div className="merchant-empty">Для мерчанта ещё не назначены валютные профили.</div>}
    {rates.map((rate) => (
      <article className="merchant-rate" key={rate.code}>
        <div><strong>USDT–{rate.code}</strong><span>{rate.name}</span></div>
        <dl>
          <div><dt>Пополнение</dt><dd>{rate.order_rate || '—'} <small>{rate.order_source || ''}</small></dd></div>
          <div><dt>Выплата</dt><dd>{rate.withdrawal_rate || '—'} <small>{rate.withdrawal_source || ''}</small></dd></div>
        </dl>
      </article>
    ))}
  </div>
);
